import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
// Print export (see PRINT_VIEW_PLAN.md). The map is WebGL — neither html2canvas
// nor html-to-image can read it — so we capture it via Cesium's own canvas and
// capture the (plain HTML/SVG) furniture via html2canvas, then composite both
// into a jsPDF page at true paper size.
export const PRINT_DPI = 200;
const MM_PER_INCH = 25.4;
// Safari caps any canvas at ~16.7 megapixels, so the largest sheets (A0/A1 at
// print DPI) fail there. Callers gate on this to show a clear message.
export const SAFARI_MAX_CANVAS_PX = 16.7e6;
export const isSafari = () => {
    const ua = navigator.userAgent;
    return /safari/i.test(ua) && !/chrome|chromium|crios|android|edg|fxios|firefox/i.test(ua);
};
// Total pixels a sheet needs at print DPI — compared against the Safari cap.
export const exportPixelCount = (wMm, hMm) => ((wMm / MM_PER_INCH) * PRINT_DPI) * ((hMm / MM_PER_INCH) * PRINT_DPI);
// Capture the live map window at print resolution, cropped to the map-window
// rect. Temporarily raises resolutionScale, renders, and reads the backing
// buffer in the SAME tick (so preserveDrawingBuffer isn't required), then
// restores. Crop ratios come from the canvas's real backing size, so it's
// robust to however Cesium applied the scale.
const captureMapWindow = (viewer, mapWindow, targetW) => {
    const scene = viewer.scene;
    const canvas = scene.canvas;
    const prevScale = viewer.resolutionScale;
    const prevBrowserRec = viewer.useBrowserRecommendedResolution;
    // Must be invoked as a METHOD on viewer — calling a detached reference loses
    // `this` and Cesium throws "_lastWidth of undefined" deep in resize().
    const doResize = () => {
        if (typeof viewer.forceResize === 'function')
            viewer.forceResize();
    };
    try {
        viewer.useBrowserRecommendedResolution = false;
        viewer.resolutionScale = Math.max(1, targetW / Math.max(1, mapWindow.width));
        doResize();
        scene.requestRender();
        viewer.render();
        const ratioX = canvas.width / Math.max(1, canvas.clientWidth);
        const ratioY = canvas.height / Math.max(1, canvas.clientHeight);
        const sx = Math.round(mapWindow.x * ratioX);
        const sy = Math.round(mapWindow.y * ratioY);
        const sw = Math.round(mapWindow.width * ratioX);
        const sh = Math.round(mapWindow.height * ratioY);
        const out = document.createElement('canvas');
        out.width = sw;
        out.height = sh;
        const ctx = out.getContext('2d');
        if (!ctx)
            return null;
        ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
        return out.toDataURL('image/jpeg', 0.92);
    }
    catch (_a) {
        return null;
    }
    finally {
        viewer.resolutionScale = prevScale;
        viewer.useBrowserRecommendedResolution = prevBrowserRec;
        doResize();
        scene.requestRender();
        viewer.render();
    }
};
export const exportPrintPdf = async (args) => {
    const { viewer, rootEl, sheet, mapWindow, paperWmm, paperHmm, fileName } = args;
    const pageWpx = Math.round((paperWmm / MM_PER_INCH) * PRINT_DPI);
    // 1. Furniture (white sheet + title block + legend) via html2canvas, cropped
    // to the sheet rect. The map window is an empty hole in this DOM, so it comes
    // out white and the map image lands on top of it during compose. The on-map
    // overlay (north + scale) is excluded here — it's captured separately so it
    // can be drawn ON TOP of the map image.
    const scale = pageWpx / Math.max(1, sheet.width);
    const furnitureCanvas = await html2canvas(rootEl, {
        x: sheet.x,
        y: sheet.y,
        width: sheet.width,
        height: sheet.height,
        scale,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        ignoreElements: (el) => { var _a, _b; return ((_b = (_a = el).getAttribute) === null || _b === void 0 ? void 0 : _b.call(_a, 'data-print-overmap')) === 'true'; },
    });
    const furnitureUrl = furnitureCanvas.toDataURL('image/jpeg', 0.95);
    // 2. Map window via Cesium hi-res capture.
    const mapTargetW = Math.round(pageWpx * (mapWindow.width / Math.max(1, sheet.width)));
    const mapUrl = captureMapWindow(viewer, mapWindow, mapTargetW);
    // 2b. On-map furniture (north + scale) as a transparent layer the size of the
    // map window — drawn over the map image so it isn't hidden by it.
    let overmapUrl = null;
    const overmapEl = rootEl.querySelector('[data-print-overmap]');
    if (overmapEl) {
        const overmapCanvas = await html2canvas(overmapEl, {
            scale,
            backgroundColor: null,
            useCORS: true,
            logging: false,
        });
        overmapUrl = overmapCanvas.toDataURL('image/png');
    }
    // 3. Compose at paper size. jsPDF forces a numeric format array to portrait
    // unless orientation is given, so pass it explicitly; then read the actual
    // page dims back for placement so it's correct regardless of any swap.
    const orientation = paperWmm >= paperHmm ? 'landscape' : 'portrait';
    const pdf = new jsPDF({ orientation, unit: 'mm', format: [paperWmm, paperHmm] });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    pdf.addImage(furnitureUrl, 'JPEG', 0, 0, pageW, pageH, undefined, 'FAST');
    const mx = ((mapWindow.x - sheet.x) / sheet.width) * pageW;
    const my = ((mapWindow.y - sheet.y) / sheet.height) * pageH;
    const mw = (mapWindow.width / sheet.width) * pageW;
    const mh = (mapWindow.height / sheet.height) * pageH;
    if (mapUrl)
        pdf.addImage(mapUrl, 'JPEG', mx, my, mw, mh, undefined, 'FAST');
    if (overmapUrl)
        pdf.addImage(overmapUrl, 'PNG', mx, my, mw, mh);
    pdf.save(fileName);
};
